import { useState, useEffect } from 'react';
import { getDeviceBatterySignal } from '../utils/api';

// useBatterySignalHistory loads one device's battery and signal series for a
// range key from HISTORY_RANGES. Unlike useChartData it fetches only
// these two series, so asking for a year — or for everything — does not also
// drag along a year of temperature, moisture, light and nutrition readings.
//
// The API returns null for a reading the device did not report, which is passed
// straight to ECharts so the line breaks there instead of bridging the gap.
//
// firstReadingAt is the device's oldest battery/signal sample, or null when it
// has none. It does not depend on the selected range, so the ranges the caller
// offers stay stable while the user switches between them.
export const useBatterySignalHistory = (deviceId, range) => {
	const [series, setSeries] = useState({ battery: [], rssi: [] });
	const [firstReadingAt, setFirstReadingAt] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// firstReadingAt describes the device, not the window, so it deliberately
	// survives a range switch: clearing it there would grey out the other ranges
	// for as long as the new one takes to load.
	useEffect(() => {
		setFirstReadingAt(null);
	}, [deviceId]);

	useEffect(() => {
		if (!deviceId) {
			setLoading(false);
			return undefined;
		}

		// A slow long-range request must not overwrite a faster one the user
		// started afterwards by switching back to a shorter range.
		let active = true;

		// Reset per device and per range, otherwise the previous series stays on
		// screen with loading already false.
		setLoading(true);
		setError(null);
		setSeries({ battery: [], rssi: [] });

		const fetchHistory = async () => {
			try {
				const response = await getDeviceBatterySignal(deviceId, range);
				if (!response.ok) {
					throw new Error('Failed to fetch battery and signal history');
				}
				const json = await response.json();
				if (!active) return;

				const rows = json.data || [];
				const at = (d) => new Date(d.datetime).getTime();
				setSeries({
					battery: rows.map((d) => [at(d), d.battery_value]),
					rssi: rows.map((d) => [at(d), d.rssi_value]),
				});
				setFirstReadingAt(json.first_reading_at || null);
				setError(null);
			} catch (err) {
				if (!active) return;
				setError(err.message);
				console.error(err);
			} finally {
				if (active) setLoading(false);
			}
		};

		fetchHistory();

		return () => {
			active = false;
		};
	}, [deviceId, range]);

	return { series, firstReadingAt, loading, error };
};

export default useBatterySignalHistory;
